import { motion } from "framer-motion";
import { useState } from "react";
import { SectionHeading } from "../components/SectionHeading";

type Service = {
  number: string;
  name: string;
  tagline: string;
  stack: string[];
  timeline: string;
  deliverable: string;
  description: string;
};

const SERVICES: Service[] = [
  {
    number: "01",
    name: "Marketing sites",
    tagline: "Conversion-grade landing & marketing pages",
    stack: ["React", "Next.js / Astro", "Tailwind", "Framer Motion", "CMS of choice"],
    timeline: "2–4 weeks",
    deliverable: "Homepage + supporting pages, content-edit ready",
    description:
      "For founders who need a site that doesn't just look great — it sells. We layer typographic confidence, motion, and bespoke art over performance budgets that make Lighthouse weep.",
  },
  {
    number: "02",
    name: "Web apps",
    tagline: "Full-stack product builds",
    stack: ["React + TS", "FastAPI / Node", "Postgres", "Auth", "Stripe"],
    timeline: "4–12 weeks",
    deliverable: "Production app with admin, auth, billing, and a deploy pipeline",
    description:
      "From a sketch in your notes app to a deployed product. We design the data, write the API, ship the UI, and don't ghost when it's live.",
  },
  {
    number: "03",
    name: "Rebuilds & migrations",
    tagline: "Modernizing sites stuck in the past",
    stack: ["Migration plan", "Modern stack swap", "Perf rewrites", "Zero-downtime cutover"],
    timeline: "3–8 weeks",
    deliverable: "Your old site, fast, beautiful, and actually maintainable",
    description:
      "Site built in 2017 by an agency that no longer exists? WordPress monstrosity bleeding load times? We rewrite it on a modern stack and hand it back without breaking your traffic or SEO.",
  },
  {
    number: "04",
    name: "Brand + site systems",
    tagline: "Identity for digital-first companies",
    stack: ["Logo + marks", "Type system", "Color palette", "Component library", "Brand voice"],
    timeline: "2–5 weeks",
    deliverable: "Brand kit + Figma library + an on-brand site to match",
    description:
      "We don't do mood boards on Pinterest. We design the kind of brand systems that make people pause when they see your link in a tweet.",
  },
];

export function Services() {
  const [active, setActive] = useState(0);
  const item = SERVICES[active];

  return (
    <section id="services" className="relative py-24 md:py-32 px-4 md:px-8">
      <div className="mx-auto max-w-[1400px]">
        <SectionHeading
          kicker="// SERVICES"
          title={
            <>
              What we make.{" "}
              <span className="shimmer-text">Pick what's closest.</span>
            </>
          }
          blurb="Here's where most projects start. Off-menu work is welcome too — get in touch and tell us what you're building."
        />

        <div className="grid lg:grid-cols-[300px_1fr] gap-6">
          {/* Service tabs */}
          <ul className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {SERVICES.map((s, i) => {
              const isActive = i === active;
              return (
                <li key={s.name} className="shrink-0 lg:shrink">
                  <button
                    type="button"
                    onClick={() => setActive(i)}
                    className={`
                      w-full text-left flex items-center gap-3 p-3 border-2
                      font-display text-[10px] uppercase tracking-wider transition-all pointer
                      ${isActive
                        ? "bg-witch text-bone border-black shadow-[4px_4px_0_0_#000]"
                        : "bg-grimoire/60 text-aura border-bruise hover:border-mystic hover:bg-bruise/60"
                      }
                    `}
                  >
                    <span
                      className={`grid place-items-center w-8 h-8 shrink-0 font-display text-xs border-2
                        ${isActive
                          ? "bg-void text-acid border-black"
                          : "bg-night text-mystic border-witch/40"
                        }`}
                    >
                      {s.number}
                    </span>
                    <span className="leading-tight">{s.name}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Active service card */}
          <motion.article
            key={item.name}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="bevel-out bg-grimoire p-1"
          >
            <div className="relative noise bg-gradient-to-br from-grimoire via-bruise to-grimoire p-6 md:p-10 border-2 border-black">
              <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-10">
                <div
                  className="shrink-0 grid place-items-center w-24 h-24 md:w-32 md:h-32 bevel-out bg-void"
                  aria-hidden
                >
                  <span className="font-display text-4xl md:text-5xl text-acid drop-shadow-[0_0_18px_rgba(155,61,245,0.8)]">
                    {item.number}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="font-terminal text-mystic text-sm uppercase tracking-widest">
                    {item.number} / {String(SERVICES.length).padStart(2, "0")}
                  </div>
                  <h3 className="mt-1 font-display text-2xl md:text-3xl text-bone leading-tight">
                    {item.name}
                  </h3>
                  <p className="mt-2 font-pixel text-lg text-aura/80">{item.tagline}</p>
                </div>
              </div>

              <p className="mt-8 font-body text-base md:text-lg text-aura/85 max-w-2xl leading-relaxed">
                {item.description}
              </p>

              <dl className="mt-10 grid sm:grid-cols-3 gap-4">
                <Cell label="Stack">
                  <ul className="flex flex-wrap gap-1.5">
                    {item.stack.map((s) => (
                      <li
                        key={s}
                        className="font-terminal text-sm text-bone bg-bruise px-2 py-0.5 border border-witch"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                </Cell>
                <Cell label="Timeline">
                  <div className="font-display text-base text-acid">{item.timeline}</div>
                </Cell>
                <Cell label="Deliverable">
                  <div className="font-pixel text-base text-bone">{item.deliverable}</div>
                </Cell>
              </dl>

              <div className="mt-10 flex justify-end">
                <a
                  href="#contact"
                  className="font-display text-[10px] uppercase tracking-widest text-mystic hover:text-bone underline decoration-dotted underline-offset-4 pointer"
                >
                  Start with this →
                </a>
              </div>
            </div>
          </motion.article>
        </div>
      </div>
    </section>
  );
}

function Cell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="bevel-in bg-void p-3">
      <dt className="font-terminal text-xs text-mystic uppercase tracking-widest mb-2">
        {label}
      </dt>
      <dd>{children}</dd>
    </div>
  );
}
